// ─────────────────────────────────────────────────────────────────────────────
// WHOLESALE BROKER AGREEMENT v2.1 — THE DOCUMENT, AS DATA.
//
// 🚨 THIS REPLACES A REFERRAL AGREEMENT WITH A WHOLESALE ONE, AND THE OLD ONE
// FORBADE THE ENTIRE NEW RELATIONSHIP. v1.0 §2.1 said no financial documents
// should be uploaded directly; §2.2 barred the Broker from collecting or
// evaluating income, asset or credit documents "on Doorly's behalf" and from
// "any activity constituting mortgage origination on behalf of Doorly". Under
// that text a broker who did the thing this channel exists for was in breach.
//
// 🔑 THE DISTINCTION THAT MAKES v2.1 COHERENT: BROKER ORIGINATES IN ITS OWN
// CAPACITY, UNDER ITS OWN LICENSE, FOR THE BORROWER — NOT AS DOORLY'S AGENT.
// The origination prohibition is gone because it was never the real concern; the
// AGENCY prohibition is retained and strengthened (§2.2 first bullet, §8). Read
// the two together before concluding anything about what a broker may do: "may
// originate" and "may act for Doorly" are opposite answers in this document.
//
// ⚠️ IT IS DATA FOR THE SAME REASON THE REALTOR INSTRUMENT IS. The partner READS
// it in DoorlyProAgreement.jsx and SIGNS a PDF from buildAgreementPdf — two
// renderers with no import path between them. Prose in both can only be compared
// by eye, which is how the loan-officer rate read 0.25% in the signed PDF and
// 1.00% on the page for three and a half months.
//
// ⚠️ ONE EXTRA TOKEN vs THE REALTOR DOCUMENT: {{ENTITY_TYPE}}. This agreement is
// executed by a company, not a person, so the preamble has a blank for the
// entity form (LLC, corporation) that the realtor instrument has no equivalent
// of. A renderer that interpolates only the realtor's four tokens leaves it
// visible on a signed contract.
//
// ⚠️ IT STOPS BEFORE THE EXECUTION BLOCK, like the realtor constant — both
// renderers draw their own signing UI. Note the signature block here asks for a
// COMPANY NMLS and an optional individual NMLS, which is the drafting tell that
// §4.1 pays the entity.
//
// ⚠️ EDITING THIS FILE CHANGES AN EXECUTED INSTRUMENT'S TEXT FOR EVERYONE WHO
// SIGNS AFTERWARDS. Bump the wholesale entry in
// constants/partnerAgreementVersion.js in the same commit.
export const WHOLESALE_BROKER_V2 = [
  {
    "kind": "p",
    "t": "This Wholesale Broker Agreement (\"Agreement\") is entered into as of {{DATE}}, by and between Go Doorly, LLC, a Delaware limited liability company with its principal place of business at 8708 S Congress Ave, Ste E500-202, Austin, TX 78745 (\"Doorly\"), and {{BROKER_NAME}}, a {{ENTITY_TYPE}} organized under the laws of the State of {{LICENSE_STATE}}, with its principal place of business at {{BROKERAGE}} (\"Broker\"). Doorly and Broker may be referred to together as the \"Parties\" and individually as a \"Party.\""
  },
  {
    "kind": "p",
    "t": "RECITALS"
  },
  {
    "kind": "p",
    "t": "WHEREAS, Doorly is a real estate company and a Texas-licensed mortgage company (NMLS #2764749) operating a same-day purchase-and-resale model supported by its Ability-First Mortgage (\"AFM\") product, which evaluates borrowers on verified income and ability-to-repay standards;"
  },
  {
    "kind": "p",
    "t": "WHEREAS, Broker is licensed as a mortgage broker or mortgage banker under the laws of each state in which it originates, and takes residential mortgage loan applications from borrowers in its own capacity and under its own licensing authority;"
  },
  {
    "kind": "p",
    "t": "WHEREAS, Broker wishes to submit completed loan applications and supporting documentation to Doorly for underwriting and funding under the AFM Program;"
  },
  {
    "kind": "p",
    "t": "WHEREAS, Doorly is the creditor and the funding source for loans it approves, and performs all underwriting, pricing, and credit decisions;"
  },
  {
    "kind": "p",
    "t": "WHEREAS, Broker performs origination services for the Borrower — including taking the application, gathering and verifying documentation, counselling the Borrower on the AFM Program, and assembling and submitting the loan file — and is compensated by Doorly for those services;"
  },
  {
    "kind": "p",
    "t": "NOW, THEREFORE, in consideration of the mutual promises and covenants set forth herein, the Parties agree as follows:"
  },
  {
    "kind": "section",
    "n": "1",
    "t": "DEFINITIONS"
  },
  {
    "kind": "clause",
    "n": "1.1",
    "t": "\"Applicable Law\"",
    "body": [
      "Any federal, state, or local statute, rule, regulation, guideline, or requirement governing residential real estate, seller financing, mortgage brokering and origination, consumer protection, disclosure, data privacy, and fair lending, including without limitation RESPA, TILA and Regulation Z, ECOA and Regulation B, the FCRA, and the SAFE Act."
    ]
  },
  {
    "kind": "clause",
    "n": "1.2",
    "t": "\"Borrower\"",
    "body": [
      "An individual whose residential mortgage loan application Broker takes and submits to Doorly for evaluation under the AFM Program."
    ]
  },
  {
    "kind": "clause",
    "n": "1.3",
    "t": "\"Loan File\"",
    "body": [
      "A complete residential mortgage loan application submitted by Broker through DoorlyPro, comprising the Uniform Residential Loan Application, the supporting income, asset and employment documentation required by Doorly’s published guidelines, the Borrower’s executed authorizations, and any consumer report Broker supplies under Section 2.4."
    ]
  },
  {
    "kind": "clause",
    "n": "1.4",
    "t": "\"Origination Services\"",
    "body": [
      "The services Broker performs for the Borrower, set out in Section 2.1, which Broker is required to perform. Origination Services include taking the loan application, gathering and reviewing supporting documentation, counselling the Borrower on the AFM Program, and assembling and submitting the Loan File."
    ]
  },
  {
    "kind": "clause",
    "n": "1.5",
    "t": "\"Broker Compensation\"",
    "body": [
      "The fee payable to Broker under Section 4 as compensation for the Origination Services actually performed, contingent upon a Successful Closing, and payable only as permitted by Applicable Law."
    ]
  },
  {
    "kind": "clause",
    "n": "1.6",
    "t": "\"Successful Closing\"",
    "body": [
      "A completed real estate purchase by Doorly and resale to the Borrower, funded and closed, with all final documents executed and recorded."
    ]
  },
  {
    "kind": "clause",
    "n": "1.7",
    "t": "\"Business Day\"",
    "body": [
      "Monday through Friday, excluding federal holidays."
    ]
  },
  {
    "kind": "clause",
    "n": "1.8",
    "t": "\"AFM Program\"",
    "body": [
      "Doorly’s Ability-First Mortgage program — a fully documented underwriting framework that verifies income, assets, credit, and ability-to-repay in accordance with Doorly’s guidelines."
    ]
  },
  {
    "kind": "section",
    "n": "2",
    "t": "BROKER ORIGINATION SERVICES AND RESPONSIBILITIES"
  },
  {
    "kind": "clause",
    "n": "2.1",
    "t": "Broker shall perform the following Origination Services",
    "body": [
      "Broker is engaged to perform, and shall perform, the following services for each Borrower, in its own capacity as a licensed mortgage broker or banker:",
      "Take a complete Uniform Residential Loan Application from the Borrower.",
      "Counsel the Borrower on the AFM Program, its requirements, and its suitability for that Borrower.",
      "Gather, review for completeness, and submit the income, asset, and employment documentation required by Doorly’s published guidelines.",
      "Obtain the Borrower’s written authorizations required for the Loan File, including any authorization required for Doorly to obtain or receive a consumer report.",
      "Assemble and submit the Loan File through DoorlyPro.",
      "Promptly provide any additional documentation Doorly requests during underwriting.",
      "Promptly correct any information Broker learns to be inaccurate.",
      "Maintain required state and NMLS licensing for each state in which it originates, and provide evidence of that licensing on request.",
      "Represent Doorly’s program accurately and in accordance with Doorly’s published guidelines.",
      "Comply with RESPA, TILA, ECOA, the FCRA, the SAFE Act, fair lending laws, and all federal and state prohibitions on steering, fee-splitting, and misrepresentation."
    ]
  },
  {
    "kind": "clause",
    "n": "2.2",
    "t": "Broker shall NOT",
    "body": [
      "Act as an agent of Doorly, or hold itself out as representing Doorly.",
      "Perform underwriting, determine credit eligibility, or make any credit decision.",
      "Quote, negotiate, or promise interest rates, pricing, loan terms, or approval outcomes.",
      "Collect any fee, deposit, or other consideration from the Borrower in connection with the AFM Program or this Agreement.",
      "Represent that Doorly has approved a Borrower before Doorly has issued its decision.",
      "Submit any Loan File containing information Broker knows or should know to be false."
    ]
  },
  {
    "kind": "clause",
    "n": "2.3",
    "t": "Allocation of disclosure responsibility",
    "body": [
      "Doorly is the creditor for purposes of TILA and Regulation Z. Doorly shall prepare and deliver the Loan Estimate and all other disclosures required of a creditor, within the timeframes Applicable Law requires.",
      "Doorly does not receive the address of the subject property from Broker. Broker shall not state a subject property address in the Loan File data it transmits, and Doorly does not request, record or rely on any subject property address that may appear within supporting documentation. Doorly obtains the subject property from the Borrower directly.",
      "Broker shall transmit each Loan File to Doorly promptly after taking the application, and in no event later than one (1) Business Day after Broker determines to submit it."
    ]
  },
  {
    "kind": "clause",
    "n": "2.4",
    "t": "Consumer reports",
    "body": [
      "Where Broker has obtained a consumer report for the Borrower, Broker may cause that report to be reissued to Doorly by the consumer reporting agency that furnished it, provided that (a) the Borrower’s written authorization permits disclosure to Doorly, (b) the reissue is effected through the consumer reporting agency and not by Broker’s transmission of a copy, and (c) Broker identifies the furnishing agency and the date the report was obtained. Doorly may decline to rely on any consumer report that does not satisfy these conditions or that does not meet the age requirements in Doorly’s published guidelines."
    ]
  },
  {
    "kind": "clause",
    "n": "2.5",
    "t": "Representations and warranties as to each Loan File",
    "body": [
      "With respect to each Loan File it submits, Broker represents and warrants that:",
      "Broker took the application from the Borrower in its own capacity, under its own licensing authority, and in compliance with Applicable Law.",
      "The information in the Loan File is true, complete, and accurate to the best of Broker’s knowledge, and Broker has no knowledge of any misrepresentation by the Borrower or any third party.",
      "All documentation was obtained from the Borrower or from an independent third-party source, and none has been altered.",
      "The Borrower’s authorizations were obtained before the documentation or consumer report to which they relate.",
      "Broker has not received, and will not receive, any fee or thing of value from the Borrower or from any third party in connection with the transaction."
    ]
  },
  {
    "kind": "clause",
    "n": "2.6",
    "t": "Repurchase and indemnity for defective Loan Files",
    "body": [
      "Where Doorly suffers a loss because a representation in Section 2.5 was untrue, Broker shall indemnify Doorly for that loss, and Broker Compensation for the affected transaction shall be repayable on demand."
    ]
  },
  {
    "kind": "section",
    "n": "3",
    "t": "DOORLY RESPONSIBILITIES"
  },
  {
    "kind": "clause",
    "n": "3.1",
    "t": "Doorly shall",
    "body": [
      "Underwrite and evaluate each submitted Loan File under its AFM guidelines.",
      "Conduct all income verification, underwriting, processing, and compliance checks.",
      "Prepare and deliver all disclosures required of a creditor, as set out in Section 2.3, within the timeframes measured from the application date Broker transmits.",
      "Act as the purchasing entity and oversee all real estate purchase and resale activities associated with the transaction.",
      "Maintain all licensing required for its operations, and maintain compliance with Applicable Law.",
      "Provide Broker high-level file status updates through DoorlyPro.",
      "Notify Broker of its decision on each Loan File, and of any additional documentation required."
    ]
  },
  {
    "kind": "clause",
    "n": "3.2",
    "t": "Doorly retains sole discretion over",
    "body": [
      "Approval or denial of any Borrower.",
      "The terms, structure, and pricing of any approved AFM transaction.",
      "Decisions related to property acquisition, resale, and financing.",
      "Whether to rely on any consumer report supplied under Section 2.4."
    ]
  },
  {
    "kind": "section",
    "n": "4",
    "t": "COMPENSATION"
  },
  {
    "kind": "clause",
    "n": "4.1",
    "t": "Structure",
    "body": [
      "In consideration of the Origination Services actually performed, Doorly shall pay Broker {{BROKER_COMP_PCT}} of the financed loan amount upon a Successful Closing, unless otherwise agreed in writing. Payment shall be made within thirty (30) days of the Successful Closing.",
      "Broker Compensation is paid to Broker as an entity. Doorly is not a party to, and has no responsibility for, any arrangement between Broker and any individual loan originator it employs or sponsors.",
      "Broker Compensation does not vary based on the interest rate, the terms of the loan, or any proxy for a term of the loan. It is calculated solely as a percentage of the amount of credit extended."
    ]
  },
  {
    "kind": "clause",
    "n": "4.2",
    "t": "Eligibility conditions",
    "body": [
      "Compensation is payable only if all of the following are satisfied:",
      "Broker performed the Origination Services set out in Section 2.1.",
      "Broker submitted the Loan File before the Borrower contacted Doorly independently.",
      "The transaction resulted in a Successful Closing.",
      "Broker has executed this Agreement and holds the then-current version of it.",
      "Broker has submitted a valid IRS Form W-9 and any other compliance documentation Doorly requires.",
      "Broker’s licensing was active and in good standing at the time of application and at closing."
    ]
  },
  {
    "kind": "clause",
    "n": "4.3",
    "t": "Restrictions",
    "body": [
      "Compensation will not be paid if:",
      "The transaction does not close, for any reason.",
      "Doorly had documented prior contact with the Borrower within the preceding twelve (12) months.",
      "The Borrower had already begun an application with Doorly, interacted with Doorly, or was already in Doorly’s system before submission.",
      "Broker submitted a Loan File for a Borrower already actively engaged with Doorly.",
      "Broker engaged in prohibited conduct, including any RESPA violation, steering, or fee-splitting.",
      "Doorly retains discretion to determine eligibility in duplicate or conflicting submission scenarios."
    ]
  },
  {
    "kind": "clause",
    "n": "4.4",
    "t": "Held compensation",
    "body": [
      "Where a Successful Closing occurs and Broker has not executed the then-current version of this Agreement, Broker Compensation is earned and held, and shall be released upon Broker’s execution of that version. Compensation held under this Section is not forfeited, and shall be calculated under the terms in force at the time of the Successful Closing."
    ]
  },
  {
    "kind": "section",
    "n": "5",
    "t": "TERM AND TERMINATION"
  },
  {
    "kind": "clause",
    "n": "5.1",
    "t": "Term",
    "body": [
      "This Agreement is effective as of the Effective Date and shall continue for one (1) year, renewing automatically unless terminated by either Party."
    ]
  },
  {
    "kind": "clause",
    "n": "5.2",
    "t": "Termination",
    "body": [
      "Either Party may terminate this Agreement with thirty (30) days’ written notice. Doorly may suspend Broker’s ability to submit Loan Files immediately, without notice, for any compliance-related concern."
    ]
  },
  {
    "kind": "clause",
    "n": "5.3",
    "t": "Effect of termination",
    "body": [
      "Broker remains eligible for Compensation on any Loan File submitted before termination that results in a Successful Closing, provided Broker performed the Origination Services for that Borrower.",
      "Broker’s obligations under Sections 2.5 and 2.6 survive termination as to every Loan File submitted.",
      "All confidentiality obligations survive termination."
    ]
  },
  {
    "kind": "section",
    "n": "6",
    "t": "CONFIDENTIALITY AND NON-DISCLOSURE"
  },
  {
    "kind": "clause",
    "n": "6.1",
    "t": "Confidential Information includes",
    "body": [
      "Borrower data",
      "Underwriting guidelines and pricing",
      "Business strategies",
      "Proprietary technology",
      "Trade secrets",
      "Non-public financial or operational data"
    ]
  },
  {
    "kind": "clause",
    "n": "6.2",
    "t": "Obligations",
    "body": [
      "Both Parties agree to:",
      "Protect confidential material",
      "Not disclose it without written consent",
      "Use it solely for purposes of this Agreement"
    ]
  },
  {
    "kind": "clause",
    "n": "6.3",
    "t": "Exceptions",
    "body": [
      "Confidential Information does not include information that:",
      "Becomes publicly available through no breach",
      "Is lawfully obtained from a third party",
      "Is independently developed without reference to Confidential Information"
    ]
  },
  {
    "kind": "clause",
    "n": "6.4",
    "t": "Required disclosure",
    "body": [
      "If legally compelled, the receiving Party must notify the other Party promptly."
    ]
  },
  {
    "kind": "clause",
    "n": "6.5",
    "t": "Return or destruction",
    "body": [
      "Upon termination, the Parties must return or destroy Confidential Information, except where retention is required by law."
    ]
  },
  {
    "kind": "clause",
    "n": "6.6",
    "t": "Remedies",
    "body": [
      "Both Parties acknowledge:",
      "Breach may cause irreparable harm",
      "Injunctive relief is appropriate",
      "All legal remedies remain available"
    ]
  },
  {
    "kind": "clause",
    "n": "6.7",
    "t": "Survival",
    "body": [
      "Confidentiality obligations survive for five (5) years after termination."
    ]
  },
  {
    "kind": "clause",
    "n": "6.8",
    "t": "Safeguarding Borrower information",
    "body": [
      "Broker holds the Borrower’s nonpublic personal information in connection with the Origination Services. Broker shall maintain administrative, technical, and physical safeguards appropriate to the sensitivity of that information, shall use it solely for the purposes of this Agreement, and shall notify Doorly promptly of any actual or suspected unauthorized access to or disclosure of it."
    ]
  },
  {
    "kind": "section",
    "n": "7",
    "t": "COMPLIANCE WITH LAWS"
  },
  {
    "kind": "p",
    "t": "Each Party warrants that it will comply with RESPA, TILA and Regulation Z, ECOA and Regulation B, the FCRA, the SAFE Act, fair lending laws, state mortgage and real estate licensing requirements, and data privacy laws. Doorly may suspend Broker for any compliance-related concern."
  },
  {
    "kind": "section",
    "n": "8",
    "t": "INDEPENDENT CONTRACTOR RELATIONSHIP"
  },
  {
    "kind": "p",
    "t": "Nothing in this Agreement creates a partnership, joint venture, employment, or agency relationship. Broker cannot bind Doorly in any capacity. Broker performs the Origination Services as an independent contractor, in its own capacity as a licensed mortgage broker or banker, using its own judgment, methods, and personnel."
  },
  {
    "kind": "section",
    "n": "9",
    "t": "INDEMNIFICATION"
  },
  {
    "kind": "p",
    "t": "Each Party agrees to indemnify and hold harmless the other Party from any claims, liabilities, losses, or damages arising out of its own breach of this Agreement or non-compliance with Applicable Law. Broker’s indemnity under Section 2.6 is in addition to, and not limited by, this Section."
  },
  {
    "kind": "section",
    "n": "10",
    "t": "GOVERNING LAW"
  },
  {
    "kind": "p",
    "t": "This Agreement shall be governed by the laws of the State of Texas, County of Travis, without regard to conflict-of-law principles."
  },
  {
    "kind": "section",
    "n": "11",
    "t": "ENTIRE AGREEMENT"
  },
  {
    "kind": "p",
    "t": "This Agreement contains the entire understanding of the Parties and supersedes all prior written or oral agreements between them on this subject matter, including any prior version of this Agreement executed by Broker."
  },
  {
    "kind": "section",
    "n": "12",
    "t": "AMENDMENTS"
  },
  {
    "kind": "p",
    "t": "No amendment to this Agreement shall be valid unless made in writing and signed by both Parties."
  },
  {
    "kind": "section",
    "n": "13",
    "t": "NOTICES"
  },
  {
    "kind": "p",
    "t": "All notices under this Agreement shall be in writing and delivered to the respective addresses of the Parties as stated above, or via email to legal@godoorly.com."
  }
];

export default WHOLESALE_BROKER_V2;
